import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Typography,
} from "@mui/material";
import ModalShell from "../ModalShell";
import { ReactComponent as Arrow } from "../../../../assets/icons/green-arrow.svg";
import { ReactComponent as Close } from "../../../../assets/icons/boxed-close.svg";
import VerticalCard from "../../cards/verticalCard/VerticalCard";
import { useNavigate } from "react-router-dom";
import { getIcon } from "../../../../utils/iconConfig";
import { useCollectionDesigns, useCollections } from "../giantModal/apis/categoryAPIs";
import { useGlobalContext } from "../../../../context/GlobalContext";
import { useInView } from "react-intersection-observer";
import {
  useAICategories,
  useAICollections,
  useAIDesigns,
} from "./aiCollectionApis";
import TabSkeleton from "../../TabSkeleton";
import { fetchItemSizes } from "../../cards/longCard/apis/itemSize";
import { DESIGN_SECTIONS } from "../../../../features/cart/api/designStats";

const AI_BORDER = "linear-gradient(120deg, #4F46E5 0%, #C026D3 55%, #F97316 100%)";

const Tile = ({ name, count, onClick, isAI, selected }) => {
  const fill = selected ? "#1B2A22" : "#0D0D0D";
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "12.2vw",
        height: "2.75vw",
        background: isAI
          ? `linear-gradient(${fill}, ${fill}) padding-box, ${AI_BORDER} border-box`
          : fill,
        border: isAI ? ".07vw solid transparent" : "none",
        color: "#FFFFFF",
        borderRadius: ".25vw",
        padding: "1vw",
        gap: ".5vw",
        cursor: "pointer",
      }}
      onClick={onClick}
    >
      <Typography fontSize={".9vw"} fontWeight={500} fontFamily={"Karla"} lineHeight="1.1vw" noWrap>
        {name}
      </Typography>
      <Typography fontSize={".9vw"} fontWeight={500} fontFamily={"Karla"} lineHeight="1.1vw">
        {count}
      </Typography>
    </div>
  );
};

const CollectionModal = ({ openModal, handleClose, embedded, tabSwitcher }) => {
  const navigate = useNavigate();
  const { cartData: globalCartData, setCartData: setSessionData, setSelectedCard } = useGlobalContext();

  const [selectedCollection, setSelectedCollection] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [allDesigns, setAllDesigns] = useState([]);

  const { data: collectionList, isLoading: loadingCollections } = useCollections();
  const { data: collectionData, isLoading: loadingDesigns } = useCollectionDesigns({
    collectionId: selectedCollection?._id,
  });

  useEffect(() => {
    if (collectionData?.designs) {
      setAllDesigns(collectionData.designs);
    }
  }, [collectionData]);

  // AI collections: list -> categories -> paginated designs, all server-side.
  const isAI = !!selectedCollection?.isAI;
  const {
    data: aiCollections = [],
    isLoading: loadingAICollections,
  } = useAICollections();
  const { data: aiCategories = [], isLoading: loadingAICategories } =
    useAICategories(selectedCollection);
  const {
    data: aiDesignPages,
    isLoading: loadingAIDesigns,
    fetchNextPage,
    hasNextPage,
  } = useAIDesigns(selectedCollection, selectedCategory);
  const { ref: ScrollRef, inView: loaderInView } = useInView({ threshold: 0.4 });

  useEffect(() => {
    if (loaderInView && hasNextPage) fetchNextPage();
  }, [loaderInView, hasNextPage, fetchNextPage]);

  const aiDesigns = useMemo(
    () => aiDesignPages?.pages?.flatMap((page) => page.designs) || [],
    [aiDesignPages]
  );

  // Collections that have cart items (for "Selected Collections" section)
  const selectedCollections = useMemo(() => {
    const map = {};
    globalCartData.forEach((d) => {
      if (!d.fromCollectionId) return;
      if (!map[d.fromCollectionId]) {
        map[d.fromCollectionId] = { id: d.fromCollectionId, name: d.fromCollectionName, count: 0 };
      }
      map[d.fromCollectionId].count += 1;
    });
    return Object.values(map);
  }, [globalCartData]);

  const selectedCollectionIds = useMemo(
    () => new Set(selectedCollections.map((c) => c.id)),
    [selectedCollections]
  );

  const remainingCount = useMemo(
    () =>
      [...(collectionList || []), ...aiCollections].filter((c) => !selectedCollectionIds.has(c._id)).length,
    [collectionList, aiCollections, selectedCollectionIds]
  );

  // Derive category groups from loaded designs + cross-ref with cart
  const categories = useMemo(() => {
    if (!allDesigns.length) return [];
    const cartStyleNums = new Set(
      globalCartData.map((c) => String(c.styleNumber).trim().toLowerCase())
    );
    const counts = {};
    const cartCounts = {};
    allDesigns.forEach((d) => {
      const cat = d.categoryName || "Other";
      counts[cat] = (counts[cat] || 0) + 1;
      if (cartStyleNums.has(String(d.styleNumber).trim().toLowerCase())) {
        cartCounts[cat] = (cartCounts[cat] || 0) + 1;
      }
    });
    const allCartCount = allDesigns.filter((d) =>
      cartStyleNums.has(String(d.styleNumber).trim().toLowerCase())
    ).length;
    return [
      { name: "All", count: allDesigns.length, cartCount: allCartCount },
      ...Object.entries(counts).map(([name, count]) => ({
        name,
        count,
        cartCount: cartCounts[name] || 0,
      })),
    ];
  }, [allDesigns, globalCartData]);

  const visibleDesigns = useMemo(() => {
    if (isAI) return aiDesigns;
    if (!selectedCategory || selectedCategory === "All") return allDesigns;
    return allDesigns.filter((d) => d.categoryName === selectedCategory);
  }, [allDesigns, selectedCategory, isAI, aiDesigns]);

  const handleClose_ = () => {
    handleClose();
    setSelectedCollection(null);
    setSelectedCategory(null);
    setAllDesigns([]);
  };

  const handleAdd = async (card) => {
    const categoryName = card?.categoryName;
    const sizeData = await fetchItemSizes({
      queryKey: [`item-sizes-${categoryName}`, { categoryName }],
    });
    let designs = JSON.parse(localStorage.getItem("cartItems")) || [];
    const newDesign = {
      ...card,
      sizeAvailable: sizeData?.length > 0,
      addSection: DESIGN_SECTIONS.COLLECTIONS,
      fromCollectionId: selectedCollection?._id,
      fromCollectionName: selectedCollection?.collectionName,
      addedAt: new Date().toISOString(),
      quantityData: [
        {
          key: `${card?.styleNumber}-${Date.now()}`,
          itemSize: card?.subCategoryName === "Without Chain" ? "N/A" : card?.itemSize || "",
          goldColor: "P",
          goldQty: "18",
          diaQty: "VS-GH",
          message: null,
          rhodium: "W",
        },
      ],
      quantity: 1,
    };
    designs.push(newDesign);
    localStorage.setItem("cartItems", JSON.stringify(designs));
    setSelectedCard(designs[designs.length - 1]?.styleNumber);
    setSessionData(JSON.parse(localStorage.getItem("cartItems")));
  };

  const handleRemove = (card) => {
    let designs = JSON.parse(localStorage.getItem("cartItems"));
    designs = designs.filter((item) => item?.styleNumber !== card?.styleNumber);
    localStorage.setItem("cartItems", JSON.stringify(designs));
    setSelectedCard(designs[designs.length - 1]?.styleNumber);
    setSessionData(JSON.parse(localStorage.getItem("cartItems")));
  };

  const isInCart = (styleNumber) =>
    globalCartData?.some(
      (item) =>
        String(item?.styleNumber).trim().toLowerCase() ===
        String(styleNumber).trim().toLowerCase()
    );

  // Level: 0 = collections, 1 = categories, 2 = designs
  const level = !selectedCollection ? 0 : !selectedCategory ? 1 : 2;

  return (
    <ModalShell embedded={embedded} open={openModal}>
        <Box
          sx={{
            position: embedded ? "relative" : "absolute",
            top: embedded ? 0 : "12%",
            background: "#141414",
            borderRadius: ".4vw",
            outline: "none",
            border: "none",
            width: embedded ? "100%" : "96vw",
            height: embedded ? "100%" : "84vh",
          }}
          marginX="auto"
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              flexGrow: 1,
              width: "100%",
              height: level === 2 ? "96%" : "100%",
            }}
          >
            {/* Header */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "1.2vw",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: "1.5vw" }}>
              {tabSwitcher}
              <Typography
                fontSize={".9vw"}
                fontWeight={500}
                fontFamily={"Karla"}
                lineHeight={"1.1vw"}
                sx={{ cursor: "pointer" }}
              >
                {level === 0 && null}
                {level === 1 && (
                  <>
                    <span
                      style={{ color: "#595959" }}
                      onClick={() => { setSelectedCollection(null); setAllDesigns([]); }}
                    >
                      Collections &nbsp;&gt;&nbsp;
                    </span>
                    <span style={{ color: "#FFFFFF" }}>
                      {selectedCollection.collectionName}
                    </span>
                  </>
                )}
                {level === 2 && (
                  <>
                    <span
                      style={{ color: "#595959" }}
                      onClick={() => { setSelectedCollection(null); setAllDesigns([]); setSelectedCategory(null); }}
                    >
                      Collections &nbsp;&gt;&nbsp;
                    </span>
                    <span
                      style={{ color: "#595959" }}
                      onClick={() => setSelectedCategory(null)}
                    >
                      {selectedCollection.collectionName} &nbsp;&gt;&nbsp;
                    </span>
                    <span style={{ color: "#FFFFFF" }}>{selectedCategory}</span>
                  </>
                )}
              </Typography>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: ".7vw" }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: ".25vw",
                    padding: ".15vw .6vw",
                    borderRadius: ".25vw",
                    background: "#262626",
                  }}
                >
                  {getIcon("Ring")}
                  <Typography fontFamily={"Karla"} fontSize={".7vw"} fontWeight={"400"} color={"#FCFAFA"}>
                    Design{globalCartData.length > 1 ? "s" : ""} :
                  </Typography>
                  <Typography fontFamily={"Poppins"} fontSize={".8vw"} fontWeight={"600"} color={"#FCFAFA"}>
                    {globalCartData.length}
                  </Typography>
                </Box>
                <Close style={{ cursor: "pointer" }} onClick={handleClose_} />
              </Box>
            </Box>

            {/* Content */}
            <Box sx={{ overflowY: "scroll", flexGrow: 1 }}>
              <Box sx={{ display: "flex", flexDirection: "column", marginBottom: "1.5vw" }}>
                <Grid
                  container
                  direction="row"
                  justifyContent="flex-start"
                  sx={{
                    flex: "1 1 1",
                    display: "flex",
                    alignItems: level === 2 ? "flex-start" : "center",
                    justifyContent: "flex-start",
                    gap: "1.6vw",
                    rowGap: "1.6vw",
                    color: "#fff",
                    padding: ".4vw 1vw",
                  }}
                >
                  {level === 0 && (
                    loadingCollections || loadingAICollections ? (
                      <TabSkeleton height={"2.75vw"} width={"12.2vw"} count={10} />
                    ) : (
                      <>
                        {/* Selected collections header */}
                        {selectedCollections.length > 0 && (
                          <Grid item xs={12} sx={{ width: "100%", paddingBottom: ".5vw" }}>
                            <Typography fontSize={".9vw"} fontWeight={500} fontFamily={"Karla"} color={"#FFFFFF"}>
                              Selected Collections - {selectedCollections.length}
                            </Typography>
                          </Grid>
                        )}

                        {/* Selected collections (have cart items) */}
                        {selectedCollections.map((col) => {
                          const full =
                            (collectionList || []).find((c) => c._id === col.id) ||
                            aiCollections.find((c) => c._id === col.id);
                          const ai = !!full?.isAI;
                          return (
                            <Tile
                              key={col.id}
                              name={col.name}
                              count={col.count}
                              isAI={ai}
                              selected
                              onClick={() => {
                                setSelectedCollection(full || { _id: col.id, collectionName: col.name });
                                setSelectedCategory(null);
                              }}
                            />
                          );
                        })}

                        {/* Divider + remaining collections */}
                        {selectedCollections.length > 0 && remainingCount > 0 && (
                          <Grid item xs={12} sx={{ width: "100%" }}>
                            <Divider
                              orientation="horizontal"
                              flexItem
                              style={{ border: ".15vw solid", borderColor: "#262626", width: "100%", margin: ".8vw 0" }}
                            />
                            <Box sx={{ paddingBottom: ".5vw" }}>
                              <Typography fontSize={".9vw"} fontWeight={500} fontFamily={"Karla"} color={"#FFFFFF"}>
                                Missed Collections - {remainingCount}
                              </Typography>
                            </Box>
                          </Grid>
                        )}

                        {/* AI collections first, then the regular ones */}
                        {aiCollections
                          .filter((col) => !selectedCollectionIds.has(col._id))
                          .map((col) => (
                            <Tile
                              key={col._id}
                              name={col.collectionName}
                              count={col.designCount || 0}
                              isAI
                              onClick={() => { setSelectedCollection(col); setSelectedCategory(null); }}
                            />
                          ))}
                        {(collectionList || [])
                          .filter((col) => !selectedCollectionIds.has(col._id))
                          .map((col) => (
                            <Tile
                              key={col._id}
                              name={col.collectionName || "Unnamed"}
                              count={col.numberOfStyles || 0}
                              onClick={() => { setSelectedCollection(col); setSelectedCategory(null); }}
                            />
                          ))}
                      </>
                    )
                  )}

                  {level === 1 && (
                    (isAI ? loadingAICategories : loadingDesigns) ? (
                      <Grid item xs={12} sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh" }}>
                        <CircularProgress color="success" />
                      </Grid>
                    ) : (
                      (isAI ? aiCategories : categories).map((cat) => (
                        <Tile
                          key={cat.name}
                          name={cat.name}
                          count={cat.count}
                          onClick={() => setSelectedCategory(cat.name)}
                        />
                      ))
                    )
                  )}

                  {level === 2 && isAI && loadingAIDesigns && (
                    <Grid item xs={12} sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh" }}>
                      <CircularProgress color="success" />
                    </Grid>
                  )}

                  {level === 2 && (
                    visibleDesigns.map((design) => (
                      <VerticalCard
                        key={design._id}
                        data={design}
                        color="#262626"
                        onAdd={handleAdd}
                        allData={visibleDesigns}
                        onRemove={handleRemove}
                        added={isInCart(design.styleNumber)}
                      />
                    ))
                  )}

                  {level === 2 && isAI && hasNextPage && (
                    <Grid item xs={12} sx={{ display: "flex", justifyContent: "center", padding: "1vw" }}>
                      <CircularProgress ref={ScrollRef} color="success" />
                    </Grid>
                  )}
                </Grid>
              </Box>
            </Box>

            {level === 0 && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  cursor: "pointer",
                  marginX: ".7vw",
                  marginBottom: ".7vw",
                  height: "2vw",
                }}
                onClick={() => navigate("/cart")}
              >
                <Button
                  sx={{
                    color: "#50C878",
                    textTransform: "none",
                    fontFamily: "Poppins",
                    fontSize: ".7vw",
                    fontWeight: "400",
                    height: "2vw !important",
                    minWidth: "7vw",
                    padding: 0,
                  }}
                >
                  Go to Cart <Arrow style={{ marginLeft: ".25vw" }} />
                </Button>
              </Box>
            )}
          </Box>

          {level === 2 && (
            <Button
              sx={{
                width: "100%",
                height: "1.75vw",
                background: "#50C878",
                color: "#000000",
                fontFamily: "Poppins",
                fontSize: ".8vw",
                fontWeight: "600",
                borderRadius: 0,
                borderBottomLeftRadius: ".4vw",
                borderBottomRightRadius: ".4vw",
              }}
              onClick={handleClose_}
            >
              PROCEED
            </Button>
          )}
        </Box>
    </ModalShell>
  );
};

export default CollectionModal;
